<template>
  <div class="detail-page">
    <!-- 헤더 -->
    <AppHeader title="" backTo="/community">
      <template #right>
        <div class="menu-wrapper">
          <button @click="showMenu = !showMenu" class="menu-btn">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 5v.01M12 12v.01M12 19v.01M12 6a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2zm0 7a1 1 0 110-2 1 1 0 010 2z" />
            </svg>
          </button>
          <!-- 드롭다운 메뉴 -->
          <div v-if="showMenu" class="dropdown-content" @click.stop>
            <!-- 작성자 메뉴 -->
            <template v-if="isAuthor">
              <button @click="handleEdit" class="dropdown-item">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                </svg>
                수정하기
              </button>
              <button @click="handleDelete" class="dropdown-item delete">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
                삭제하기
              </button>
              <div class="dropdown-divider"></div>
            </template>
            <!-- 공통 메뉴 -->
            <button @click="handleBookmark" class="dropdown-item" :disabled="!isLoggedIn">
              <svg viewBox="0 0 24 24" :fill="isBookmarked ? 'currentColor' : 'none'" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
              </svg>
              {{ isBookmarked ? '북마크 해제' : '북마크' }}
            </button>
            <button v-if="!isAuthor && isLoggedIn" @click="openReportModal" class="dropdown-item report">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
              신고하기
            </button>
          </div>
          <!-- 바깥 클릭 감지용 오버레이 -->
          <div v-if="showMenu" class="dropdown-overlay" @click="showMenu = false"></div>
        </div>
      </template>
    </AppHeader>

    <!-- 로딩 -->
    <div v-if="loading" class="loading-state">
      <div class="loading-spinner"></div>
    </div>

    <!-- 게시글 내용 -->
    <div v-else-if="post" class="content-wrapper">
      <!-- 게시글 -->
      <div class="post-section">
        <div class="post-meta">
          <span class="post-category">{{ getCategoryLabel(post.category) }}</span>
          <span class="post-time">{{ post.timeAgo }}</span>
        </div>
        <h1 class="post-title">{{ post.title }}</h1>
        <div class="post-author-row">
          <span class="post-author">{{ post.maskedEmail }}</span>
          <span class="post-views">조회 {{ post.viewCount || 0 }}</span>
        </div>
        <div class="post-content">{{ post.content }}</div>

        <!-- 이미지 갤러리 -->
        <div v-if="post.images && post.images.length > 0" class="image-gallery">
          <div
            v-for="(img, index) in post.images"
            :key="index"
            class="gallery-item"
            @click="openImageModal(index)"
          >
            <img :src="img" :alt="`이미지 ${index + 1}`" />
          </div>
        </div>

        <!-- 좋아요/북마크/신고 버튼 -->
        <div class="action-section">
          <button
            :class="['action-btn', { active: isLiked }]"
            @click="handleLike"
            :disabled="!isLoggedIn"
          >
            <svg viewBox="0 0 24 24" :fill="isLiked ? 'currentColor' : 'none'" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
            <span>{{ post.likeCount || 0 }}</span>
          </button>
          <button
            :class="['action-btn bookmark', { active: isBookmarked }]"
            @click="handleBookmark"
            :disabled="!isLoggedIn"
          >
            <svg viewBox="0 0 24 24" :fill="isBookmarked ? 'currentColor' : 'none'" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
            </svg>
            <span>{{ post.bookmarkCount || 0 }}</span>
          </button>
          <button
            v-if="!isAuthor"
            class="action-btn report"
            @click="openReportModal"
            :disabled="!isLoggedIn"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span>신고</span>
          </button>
        </div>
      </div>

      <!-- 댓글 섹션 -->
      <div class="comment-section">
        <div class="comment-header">
          <h3 class="section-title">
            댓글
            <span class="comment-count">{{ post.commentCount || 0 }}</span>
          </h3>
        </div>

        <!-- 댓글 입력 -->
        <div v-if="isLoggedIn" class="comment-input-box">
          <textarea
            v-model="newComment"
            placeholder="댓글을 입력하세요"
            rows="1"
            class="comment-textarea"
          ></textarea>
          <button
            @click="submitComment"
            :disabled="!newComment.trim() || commentLoading"
            class="comment-submit-btn"
          >
            {{ commentLoading ? '...' : '등록' }}
          </button>
        </div>
        <div v-else class="login-prompt">
          <p>댓글을 작성하려면 로그인이 필요합니다.</p>
          <button @click="goToLogin" class="login-btn">로그인</button>
        </div>

        <!-- 댓글 목록 -->
        <div class="comment-list">
          <div v-if="comments.length === 0" class="no-comments">
            <p>아직 댓글이 없습니다.</p>
          </div>

          <div v-for="comment in comments" :key="comment.id" class="comment-item">
            <!-- 댓글 본문 -->
            <div class="comment-main">
              <div class="comment-meta">
                <span class="comment-author">{{ comment.maskedEmail }}</span>
                <span class="comment-time">{{ comment.timeAgo }}</span>
              </div>
              <p class="comment-content">{{ comment.content }}</p>
              <div class="comment-actions">
                <button @click="openReplyInput(comment.id)" class="comment-action-btn">
                  답글
                </button>
                <button
                  v-if="comment.replyCount > 0"
                  @click="toggleReplies(comment.id)"
                  class="comment-action-btn reply-toggle"
                >
                  {{ expandedReplies[comment.id] ? '답글 숨기기' : `답글 ${comment.replyCount}개 보기` }}
                </button>
                <button
                  v-if="isLoggedIn && user?.uid === comment.userId"
                  @click="handleDeleteComment(comment.id)"
                  class="comment-action-btn delete"
                >
                  삭제
                </button>
              </div>
            </div>

            <!-- 답글 입력 -->
            <div v-if="replyingTo === comment.id" class="reply-input-box">
              <textarea
                v-model="replyContent"
                placeholder="답글을 입력하세요"
                rows="1"
                class="reply-textarea"
              ></textarea>
              <div class="reply-actions">
                <button @click="replyingTo = null" class="reply-cancel-btn">취소</button>
                <button
                  @click="submitReply(comment.id)"
                  :disabled="!replyContent.trim() || commentLoading"
                  class="reply-submit-btn"
                >
                  {{ commentLoading ? '등록 중...' : '답글 등록' }}
                </button>
              </div>
            </div>

            <!-- 대댓글 목록 -->
            <div v-if="expandedReplies[comment.id]" class="reply-list">
              <div v-if="loadingReplies[comment.id]" class="reply-loading">
                <div class="loading-spinner small"></div>
              </div>
              <div v-else v-for="reply in expandedReplies[comment.id]" :key="reply.id" class="reply-item">
                <div class="comment-meta">
                  <span class="comment-author">{{ reply.maskedEmail }}</span>
                  <span class="comment-time">{{ reply.timeAgo }}</span>
                </div>
                <p class="comment-content">{{ reply.content }}</p>
                <div class="comment-actions">
                  <button
                    v-if="isLoggedIn && user?.uid === reply.userId"
                    @click="handleDeleteComment(reply.id, comment.id)"
                    class="comment-action-btn delete"
                  >
                    삭제
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- 블라인드 처리된 게시글 -->
    <div v-else-if="post?.isBlinded" class="blinded-state">
      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
      <p>신고로 인해 숨겨진 게시글입니다.</p>
      <button @click="$router.push('/community')" class="back-btn">목록으로</button>
    </div>

    <!-- 에러 -->
    <div v-else class="error-state">
      <p>게시글을 찾을 수 없습니다.</p>
      <button @click="$router.push('/community')" class="back-btn">목록으로</button>
    </div>

    <!-- 이미지 확대 모달 -->
    <div v-if="showImageModal" class="image-modal-overlay" @click="closeImageModal">
      <button class="image-modal-close" @click="closeImageModal">
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <path d="M18 6L6 18M6 6l12 12" />
        </svg>
      </button>
      <div class="image-modal-content" @click.stop>
        <img :src="post.images[currentImageIndex]" alt="확대 이미지" />
      </div>
      <div v-if="post.images.length > 1" class="image-modal-nav">
        <button
          class="nav-btn prev"
          @click.stop="prevImage"
          :disabled="currentImageIndex === 0"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <span class="image-counter">{{ currentImageIndex + 1 }} / {{ post.images.length }}</span>
        <button
          class="nav-btn next"
          @click.stop="nextImage"
          :disabled="currentImageIndex === post.images.length - 1"
        >
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M9 5l7 7-7 7" />
          </svg>
        </button>
      </div>
    </div>

    <!-- 신고 모달 -->
    <div v-if="showReportModal" class="modal-overlay" @click="showReportModal = false">
      <div class="modal-content report-modal" @click.stop>
        <h3 class="modal-title">게시글 신고</h3>
        <p class="modal-desc">신고 사유를 선택해주세요.</p>

        <div class="report-options">
          <label
            v-for="reason in reportReasons"
            :key="reason.id"
            :class="['report-option', { selected: selectedReason === reason.id }]"
          >
            <input
              type="radio"
              :value="reason.id"
              v-model="selectedReason"
              class="report-radio"
            />
            <span>{{ reason.label }}</span>
          </label>
        </div>

        <textarea
          v-if="selectedReason === 'other'"
          v-model="reportDetail"
          placeholder="신고 사유를 입력해주세요"
          class="report-textarea"
          rows="3"
        ></textarea>

        <div class="modal-buttons">
          <button @click="showReportModal = false" class="modal-btn cancel">취소</button>
          <button
            @click="submitReport"
            :disabled="!selectedReason || reportLoading"
            class="modal-btn confirm"
          >
            {{ reportLoading ? '신고 중...' : '신고하기' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import AppHeader from '@/components/common/AppHeader.vue'
import { useCommunity, COMMUNITY_CATEGORIES, REPORT_REASONS } from '@/composables/main/useCommunity'
import { useAuth } from '@/composables/common/useAuth'

const router = useRouter()
const route = useRoute()
const {
  getPost,
  deletePost,
  getComments,
  getReplies,
  createComment,
  deleteComment,
  toggleLike,
  checkLiked,
  toggleBookmark,
  checkBookmarked,
  reportPost,
  loading
} = useCommunity()
const { user, isLoggedIn } = useAuth()

const post = ref(null)
const comments = ref([])
const isLiked = ref(false)
const isBookmarked = ref(false)
const showMenu = ref(false)
const newComment = ref('')
const replyContent = ref('')
const replyingTo = ref(null)
const expandedReplies = ref({})
const loadingReplies = ref({})
const commentLoading = ref(false)

// 신고 관련
const reportReasons = REPORT_REASONS
const showReportModal = ref(false)
const selectedReason = ref('')
const reportDetail = ref('')
const reportLoading = ref(false)

// 이미지 모달 관련
const showImageModal = ref(false)
const currentImageIndex = ref(0)

// 작성자인지 확인
const isAuthor = computed(() => {
  return isLoggedIn.value && user.value?.uid === post.value?.userId
})

// 카테고리 라벨 가져오기
const getCategoryLabel = (categoryId) => {
  const cat = COMMUNITY_CATEGORIES.find(c => c.id === categoryId)
  return cat ? cat.label : categoryId
}

// 게시글 로드
const loadPost = async () => {
  const postId = route.params.id
  post.value = await getPost(postId)

  if (post.value && isLoggedIn.value) {
    isLiked.value = await checkLiked(postId, user.value.uid)
    isBookmarked.value = await checkBookmarked(postId, user.value.uid)
  }
}

// 댓글 로드
const loadComments = async () => {
  const postId = route.params.id
  comments.value = await getComments(postId)
}

// 좋아요
const handleLike = async () => {
  if (!isLoggedIn.value) return

  const result = await toggleLike(route.params.id, user.value.uid)
  if (result.success) {
    isLiked.value = result.liked
    post.value.likeCount += result.liked ? 1 : -1
  }
}

// 북마크
const handleBookmark = async () => {
  if (!isLoggedIn.value) return
  showMenu.value = false

  const result = await toggleBookmark(route.params.id, user.value.uid)
  if (result.success) {
    isBookmarked.value = result.bookmarked
    post.value.bookmarkCount = (post.value.bookmarkCount || 0) + (result.bookmarked ? 1 : -1)
  }
}

// 신고 모달 열기
const openReportModal = () => {
  showMenu.value = false
  selectedReason.value = ''
  reportDetail.value = ''
  showReportModal.value = true
}

// 신고 제출
const submitReport = async () => {
  if (!selectedReason.value || !isLoggedIn.value) return

  reportLoading.value = true
  const result = await reportPost({
    postId: route.params.id,
    userId: user.value.uid,
    userEmail: user.value.email,
    reason: selectedReason.value,
    reasonDetail: reportDetail.value
  })

  reportLoading.value = false
  showReportModal.value = false

  if (result.success) {
    if (result.blinded) {
      alert('신고가 접수되었습니다. 해당 게시글은 블라인드 처리되었습니다.')
      router.push('/community')
    } else {
      alert('신고가 접수되었습니다.')
    }
  } else {
    alert(result.error || '신고에 실패했습니다.')
  }
}

// 댓글 작성
const submitComment = async () => {
  if (!newComment.value.trim() || !isLoggedIn.value) return

  commentLoading.value = true
  const result = await createComment({
    postId: route.params.id,
    userId: user.value.uid,
    userEmail: user.value.email,
    content: newComment.value.trim()
  })

  if (result.success) {
    newComment.value = ''
    post.value.commentCount++
    await loadComments()
  }
  commentLoading.value = false
}

// 답글 입력 열기
const openReplyInput = (commentId) => {
  replyingTo.value = commentId
  replyContent.value = ''
}

// 답글 작성
const submitReply = async (parentId) => {
  if (!replyContent.value.trim() || !isLoggedIn.value) return

  commentLoading.value = true
  const result = await createComment({
    postId: route.params.id,
    userId: user.value.uid,
    userEmail: user.value.email,
    content: replyContent.value.trim(),
    parentId
  })

  if (result.success) {
    replyContent.value = ''
    replyingTo.value = null
    post.value.commentCount++
    await loadComments()
    // 해당 댓글의 답글 새로고침
    if (expandedReplies.value[parentId]) {
      await loadRepliesForComment(parentId)
    }
  }
  commentLoading.value = false
}

// 답글 토글
const toggleReplies = async (commentId) => {
  if (expandedReplies.value[commentId]) {
    delete expandedReplies.value[commentId]
  } else {
    await loadRepliesForComment(commentId)
  }
}

// 특정 댓글의 답글 로드
const loadRepliesForComment = async (commentId) => {
  loadingReplies.value[commentId] = true
  try {
    const replies = await getReplies(commentId)
    expandedReplies.value[commentId] = replies
  } finally {
    loadingReplies.value[commentId] = false
  }
}

// 댓글 삭제
const handleDeleteComment = async (commentId, parentId = null) => {
  if (!confirm('댓글을 삭제하시겠습니까?')) return

  const result = await deleteComment(commentId, route.params.id, parentId)
  if (result.success) {
    await loadComments()
    await loadPost()
    if (parentId && expandedReplies.value[parentId]) {
      await loadRepliesForComment(parentId)
    }
  }
}

// 수정
const handleEdit = () => {
  showMenu.value = false
  router.push(`/community/write?edit=${route.params.id}`)
}

// 삭제
const handleDelete = async () => {
  showMenu.value = false
  if (!confirm('게시글을 삭제하시겠습니까?\n삭제된 게시글은 복구할 수 없습니다.')) return

  const result = await deletePost(route.params.id)
  if (result.success) {
    router.replace('/community')
  } else {
    alert('삭제에 실패했습니다.')
  }
}

// 로그인 페이지로 이동
const goToLogin = () => {
  router.push('/login')
}

// 이미지 모달 열기
const openImageModal = (index) => {
  currentImageIndex.value = index
  showImageModal.value = true
}

// 이미지 모달 닫기
const closeImageModal = () => {
  showImageModal.value = false
}

// 이전 이미지
const prevImage = () => {
  if (currentImageIndex.value > 0) {
    currentImageIndex.value--
  }
}

// 다음 이미지
const nextImage = () => {
  if (post.value?.images && currentImageIndex.value < post.value.images.length - 1) {
    currentImageIndex.value++
  }
}

onMounted(async () => {
  await loadPost()
  await loadComments()
})
</script>

<style scoped>
.detail-page {
  min-height: 100vh;
  background: #ffffff;
  padding-bottom: 80px;
}

.detail-page :deep(header) {
  position: sticky;
  top: 0;
  z-index: 100;
}

/* 메뉴 래퍼 */
.menu-wrapper {
  position: relative;
}

/* 메뉴 버튼 */
.menu-btn {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: none;
  border: none;
  cursor: pointer;
}

.menu-btn svg {
  width: 20px;
  height: 20px;
  color: #64748b;
}

/* 드롭다운 오버레이 (바깥 클릭 감지) */
.dropdown-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 199;
}

/* 드롭다운 메뉴 */
.dropdown-content {
  position: absolute;
  top: 100%;
  right: 0;
  margin-top: 4px;
  min-width: 160px;
  background: white;
  border-radius: 12px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.15);
  overflow: hidden;
  z-index: 200;
}

.dropdown-item {
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  padding: 14px 20px;
  font-size: 14px;
  color: #1e293b;
  background: none;
  border: none;
  cursor: pointer;
  transition: background 0.2s;
}

.dropdown-item:hover {
  background: #f8fafc;
}

.dropdown-item.delete {
  color: #ef4444;
}

.dropdown-item.report {
  color: #f59e0b;
}

.dropdown-item:disabled {
  color: #cbd5e1;
  cursor: not-allowed;
}

.dropdown-item svg {
  width: 18px;
  height: 18px;
}

.dropdown-divider {
  height: 1px;
  background: #e2e8f0;
  margin: 4px 0;
}

/* 로딩 */
.loading-state {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
}

.loading-spinner {
  width: 32px;
  height: 32px;
  border: 3px solid #e2e8f0;
  border-top-color: #2563eb;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

.loading-spinner.small {
  width: 20px;
  height: 20px;
  border-width: 2px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* 게시글 섹션 */
.content-wrapper {
  padding-bottom: 40px;
  max-width: 480px;
  margin: 0 auto;
}

.post-section {
  background: white;
  padding: 20px 16px;
  margin-top: 12px;
  margin-bottom: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
  border-radius: 16px;
}

.post-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.post-category {
  font-size: 12px;
  font-weight: 600;
  color: #2563eb;
  padding: 4px 10px;
  background: #dbeafe;
  border-radius: 6px;
}

.post-time {
  font-size: 12px;
  color: #94a3b8;
}

.post-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 12px 0;
  line-height: 1.4;
}

.post-author-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding-bottom: 16px;
  border-bottom: 1px solid #f1f5f9;
  margin-bottom: 16px;
}

.post-author {
  font-size: 13px;
  color: #64748b;
}

.post-views {
  font-size: 13px;
  color: #94a3b8;
}

.post-content {
  font-size: 15px;
  color: #1e293b;
  line-height: 1.7;
  white-space: pre-wrap;
  word-break: break-word;
}

/* 이미지 갤러리 */
.image-gallery {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
  margin-top: 16px;
}

.gallery-item {
  aspect-ratio: 1;
  border-radius: 12px;
  overflow: hidden;
  cursor: pointer;
  transition: transform 0.2s;
}

.gallery-item:hover {
  transform: scale(1.02);
}

.gallery-item img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

/* 이미지가 1개일 때 */
.image-gallery:has(.gallery-item:only-child) {
  grid-template-columns: 1fr;
}

.image-gallery:has(.gallery-item:only-child) .gallery-item {
  aspect-ratio: 16/9;
}

/* 이미지가 2개일 때 */
.image-gallery:has(.gallery-item:nth-child(2):last-child) {
  grid-template-columns: repeat(2, 1fr);
}

/* 좋아요/북마크 액션 버튼 */
.action-section {
  display: flex;
  justify-content: center;
  gap: 12px;
  padding-top: 20px;
  margin-top: 20px;
  border-top: 1px solid #f1f5f9;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 10px 16px;
  font-size: 13px;
  font-weight: 500;
  color: #64748b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 20px;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover:not(:disabled) {
  background: #f1f5f9;
}

.action-btn.active {
  background: #fee2e2;
  border-color: #ef4444;
  color: #ef4444;
}

.action-btn.bookmark.active {
  background: #dbeafe;
  border-color: #2563eb;
  color: #2563eb;
}

.action-btn.report {
  color: #f59e0b;
}

.action-btn.report:hover:not(:disabled) {
  background: #fef3c7;
  border-color: #f59e0b;
}

.action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.action-btn svg {
  width: 18px;
  height: 18px;
}

/* 댓글 섹션 */
.comment-section {
  background: white;
  padding: 20px 16px;
  margin-top: 8px;
  border-radius: 16px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.comment-header {
  margin-bottom: 16px;
}

.section-title {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 16px;
  font-weight: 700;
  color: #1e293b;
  margin: 0;
}

.comment-count {
  font-size: 14px;
  font-weight: 600;
  color: #2563eb;
}

/* 댓글 입력 */
.comment-input-box {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  background: #f1f5f9;
  border-radius: 12px;
  margin-bottom: 20px;
}

.comment-textarea {
  flex: 1;
  border: none;
  background: transparent;
  resize: none;
  font-size: 14px;
  color: #1e293b;
  outline: none;
  line-height: 1.4;
}

.comment-textarea::placeholder {
  color: #94a3b8;
}

.comment-submit-btn {
  flex-shrink: 0;
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.2s;
}

.comment-submit-btn:hover:not(:disabled) {
  background: #1d4ed8;
}

.comment-submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 로그인 유도 */
.login-prompt {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  background: #f8fafc;
  border-radius: 12px;
  margin-bottom: 20px;
}

.login-prompt p {
  font-size: 14px;
  color: #64748b;
  margin: 0;
}

.login-btn {
  padding: 8px 16px;
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  background: white;
  border: 1px solid #2563eb;
  border-radius: 8px;
  cursor: pointer;
}

/* 댓글 목록 */
.comment-list {
  display: flex;
  flex-direction: column;
}

.no-comments {
  text-align: center;
  padding: 32px 16px;
  color: #94a3b8;
  font-size: 14px;
}

.comment-item {
  padding: 16px 0;
  border-bottom: 1px solid #f1f5f9;
}

.comment-item:last-child {
  border-bottom: none;
}

.comment-main {
  padding: 0;
}

.comment-meta {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 6px;
}

.comment-author {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
}

.comment-time {
  font-size: 12px;
  color: #94a3b8;
}

.comment-content {
  font-size: 14px;
  color: #1e293b;
  line-height: 1.5;
  margin: 0 0 8px 0;
  white-space: pre-wrap;
}

.comment-actions {
  display: flex;
  align-items: center;
  gap: 12px;
}

.comment-action-btn {
  font-size: 12px;
  color: #94a3b8;
  background: none;
  border: none;
  cursor: pointer;
  padding: 0;
}

.comment-action-btn:hover {
  color: #64748b;
}

.comment-action-btn.reply-toggle {
  color: #2563eb;
}

.comment-action-btn.delete {
  color: #ef4444;
}

/* 답글 입력 */
.reply-input-box {
  margin-top: 12px;
  margin-left: 20px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 10px;
}

.reply-textarea {
  width: 100%;
  border: none;
  background: transparent;
  resize: none;
  font-size: 13px;
  color: #1e293b;
  outline: none;
  line-height: 1.4;
  margin-bottom: 8px;
}

.reply-textarea::placeholder {
  color: #94a3b8;
}

.reply-actions {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
}

.reply-cancel-btn {
  padding: 6px 12px;
  font-size: 12px;
  color: #64748b;
  background: none;
  border: none;
  cursor: pointer;
}

.reply-submit-btn {
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 600;
  color: white;
  background: #2563eb;
  border: none;
  border-radius: 6px;
  cursor: pointer;
}

.reply-submit-btn:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 대댓글 목록 */
.reply-list {
  margin-top: 12px;
  margin-left: 20px;
  padding-left: 16px;
  border-left: 2px solid #e2e8f0;
}

.reply-loading {
  display: flex;
  justify-content: center;
  padding: 12px;
}

.reply-item {
  padding: 12px 0;
}

.reply-item:first-child {
  padding-top: 0;
}

/* 에러/블라인드 상태 */
.error-state,
.blinded-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 300px;
  color: #64748b;
  text-align: center;
  padding: 20px;
}

.blinded-state svg {
  width: 48px;
  height: 48px;
  color: #cbd5e1;
  margin-bottom: 16px;
}

.error-state p,
.blinded-state p {
  font-size: 14px;
  margin-bottom: 16px;
}

.back-btn {
  padding: 10px 24px;
  font-size: 14px;
  font-weight: 500;
  color: #2563eb;
  background: white;
  border: 1px solid #2563eb;
  border-radius: 8px;
  cursor: pointer;
}

/* 신고 모달 */
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 200;
  padding: 20px;
}

.modal-content {
  width: 100%;
  max-width: 340px;
  background: white;
  border-radius: 20px;
  padding: 24px;
}

.modal-title {
  font-size: 18px;
  font-weight: 700;
  color: #1e293b;
  margin: 0 0 8px 0;
  text-align: center;
}

.modal-desc {
  font-size: 14px;
  color: #64748b;
  margin: 0 0 20px 0;
  text-align: center;
}

.report-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.report-option {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 14px;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s;
}

.report-option.selected {
  background: #fef3c7;
  border-color: #f59e0b;
}

.report-radio {
  width: 18px;
  height: 18px;
  accent-color: #f59e0b;
}

.report-option span {
  font-size: 14px;
  color: #1e293b;
}

.report-textarea {
  width: 100%;
  padding: 12px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 10px;
  resize: none;
  outline: none;
  margin-bottom: 16px;
}

.report-textarea:focus {
  border-color: #f59e0b;
}

.modal-buttons {
  display: flex;
  gap: 12px;
}

.modal-btn {
  flex: 1;
  padding: 14px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s;
}

.modal-btn.cancel {
  color: #64748b;
  background: #f1f5f9;
}

.modal-btn.confirm {
  color: white;
  background: #f59e0b;
}

.modal-btn.confirm:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

/* 이미지 확대 모달 */
.image-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 300;
}

.image-modal-close {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
  z-index: 310;
}

.image-modal-close:hover {
  background: rgba(255, 255, 255, 0.2);
}

.image-modal-close svg {
  width: 24px;
  height: 24px;
  color: white;
}

.image-modal-content {
  max-width: 90%;
  max-height: 80vh;
  display: flex;
  align-items: center;
  justify-content: center;
}

.image-modal-content img {
  max-width: 100%;
  max-height: 80vh;
  object-fit: contain;
  border-radius: 8px;
}

.image-modal-nav {
  display: flex;
  align-items: center;
  gap: 20px;
  margin-top: 20px;
}

.nav-btn {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 0.1);
  border: none;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.2s;
}

.nav-btn:hover:not(:disabled) {
  background: rgba(255, 255, 255, 0.2);
}

.nav-btn:disabled {
  opacity: 0.3;
  cursor: not-allowed;
}

.nav-btn svg {
  width: 24px;
  height: 24px;
  color: white;
}

.image-counter {
  font-size: 14px;
  color: white;
  font-weight: 500;
}
</style>
